import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND_FULL_NAME, BRAND_TEACHER, THEME } from '../../config/brand';
import heroTeacher from '../../assets/images/teachers/omar-elkholy.png';
import heroTriangle from '../../assets/images/hero/triangle-4.png';

const NAVY = THEME.navy;
const NAVY_DEEP = THEME.deep;
const NAVY_INK = THEME.ink;
const ACCENT = THEME.accent;
const ACCENT_RGB = THEME.accentRgb;

const HomeHero: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const waveRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = waveRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let running = true;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const particles = Array.from({ length: 56 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: 1.1 + Math.random() * 2.6,
      speed: 0.1 + Math.random() * 0.38,
      phase: Math.random() * Math.PI * 2,
      amp: 8 + Math.random() * 20,
    }));

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const { width, height } = parent.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    const draw = (t: number) => {
      if (!running) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      const time = t * 0.001;
      particles.forEach((p, i) => {
        const x = ((p.x * w + time * p.speed * 42 + i * 7) % (w + 40)) - 20;
        const y =
          h * 0.32 +
          p.y * h * 0.58 +
          Math.sin(time * p.speed * 2.2 + p.phase) * p.amp;
        const alpha = 0.2 + 0.42 * (0.5 + 0.5 * Math.sin(time + p.phase));
        ctx.beginPath();
        ctx.fillStyle = `rgba(${ACCENT_RGB}, ${alpha})`;
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });

      for (let band = 0; band < 3; band += 1) {
        ctx.beginPath();
        const baseY = h * (0.4 + band * 0.17);
        for (let x = 0; x <= w; x += 8) {
          const y =
            baseY +
            Math.sin(x * 0.008 + time * (0.9 + band * 0.2) + band) * (11 + band * 4) +
            Math.sin(x * 0.02 - time * 0.7) * 4;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(${ACCENT_RGB}, ${0.22 - band * 0.05})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section
      id="hero"
      aria-labelledby="hero-heading"
      className="relative isolate min-h-[100svh] w-full overflow-hidden pt-[4.75rem]"
      style={{ background: NAVY_INK }}
      dir="rtl"
    >
      <style>{`
        @keyframes hero-rise {
          from { opacity: 0; transform: translateY(28px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hero-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }
        @keyframes hero-spin-slow {
          from { transform: rotate(-12deg); }
          to { transform: rotate(348deg); }
        }
        @keyframes hero-glow-pulse {
          0%, 100% { opacity: 0.55; transform: translateX(-50%) scale(1); }
          50% { opacity: 0.9; transform: translateX(-50%) scale(1.08); }
        }
        .hero-rise { animation: hero-rise 0.85s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .hero-rise-delay { animation: hero-rise 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.12s both; }
        .hero-rise-delay-2 { animation: hero-rise 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.24s both; }
        .hero-rise-delay-3 { animation: hero-rise 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.36s both; }
        .hero-float { animation: hero-float 7.5s ease-in-out infinite; }
        .hero-spin-slow { animation: hero-spin-slow 32s linear infinite; }
        .hero-glow-pulse { animation: hero-glow-pulse 6s ease-in-out infinite; }

        /* WebView-safe RTL title */
        .hero-copy {
          direction: rtl;
          text-align: right;
          unicode-bidi: isolate;
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }
        .hero-title {
          direction: rtl;
          unicode-bidi: plaintext;
          text-align: right;
          writing-mode: horizontal-tb;
          word-break: keep-all;
          overflow-wrap: normal;
          white-space: normal;
          font-feature-settings: "locl" 1;
        }
        .hero-title-line {
          display: block;
          direction: rtl;
          unicode-bidi: isolate;
          text-align: right;
          width: 100%;
        }
        .hero-eyebrow {
          direction: rtl;
          unicode-bidi: isolate;
          text-align: right;
          text-transform: none;
          letter-spacing: 0.12em;
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-rise, .hero-rise-delay, .hero-rise-delay-2, .hero-rise-delay-3,
          .hero-float, .hero-spin-slow, .hero-glow-pulse { animation: none !important; }
        }
      `}</style>

      {/* Base + grid boxes background */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: `linear-gradient(180deg, ${NAVY_INK} 0%, ${NAVY_DEEP} 45%, ${NAVY} 100%)` }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(${ACCENT_RGB}, 0.14) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(${ACCENT_RGB}, 0.14) 1px, transparent 1px)
          `,
          backgroundSize: '72px 72px',
          backgroundPosition: '0 0',
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(${ACCENT_RGB}, 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(${ACCENT_RGB}, 0.06) 1px, transparent 1px)
          `,
          backgroundSize: '24px 24px',
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 65% 55% at 22% 55%, rgba(${ACCENT_RGB}, 0.16) 0%, transparent 60%),
            radial-gradient(ellipse 40% 35% at 80% 20%, rgba(${ACCENT_RGB}, 0.08) 0%, transparent 55%),
            radial-gradient(ellipse 90% 45% at 50% 100%, rgba(7, 11, 24, 0.75) 0%, transparent 60%)
          `,
        }}
        aria-hidden
      />

      <div className="relative z-10 mx-auto grid min-h-[calc(100svh-4.75rem)] w-full max-w-[1720px] grid-cols-1 items-end lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.18fr)] lg:items-center">
        {/* Copy — forced RTL for browser + WebView */}
        <div
          className="hero-copy relative z-20 flex w-full flex-col items-stretch px-5 pb-4 pt-6 sm:px-10 lg:px-12 lg:pb-24 lg:pt-10 xl:px-20"
          dir="rtl"
          lang="ar"
        >
          <p
            className="hero-eyebrow hero-rise w-full font-display text-[11px] sm:text-xs"
            style={{ color: ACCENT }}
          >
            {BRAND_FULL_NAME}
          </p>

          <h1
            id="hero-heading"
            className="hero-title hero-rise-delay mt-4 w-full font-display lg:mt-5"
            dir="rtl"
            lang="ar"
            style={{
              fontSize: 'clamp(1.85rem, 0.7rem + 3.8vw, 3.75rem)',
              lineHeight: 1.25,
              color: '#F5F7FF',
              textShadow: `0 0 18px rgba(${ACCENT_RGB}, 0.45)`,
            }}
          >
            <span className="relative inline-block whitespace-nowrap">
              طريقك لتقفيل الإنجليزي
              <span
                className="absolute -bottom-1 end-0 h-[3px] w-[38%] rounded-full"
                style={{
                  background: `linear-gradient(to left, ${ACCENT}, transparent)`,
                  boxShadow: `0 0 10px rgba(${ACCENT_RGB}, 0.55)`,
                }}
                aria-hidden
              />
            </span>
          </h1>

          <p
            className="hero-rise-delay-2 mt-7 w-full max-w-[34ch] self-start text-[1.15rem] leading-9 text-[#C5CFE9] sm:text-[1.25rem] sm:leading-10 lg:mt-8 lg:text-[1.35rem] lg:leading-[1.85]"
            dir="rtl"
            lang="ar"
          >
            شرح واضح، كورسات مرتّبة، ومتابعة تقدّمك من أول يوم.
          </p>

          <div className="hero-rise-delay-3 mt-8 flex w-full flex-wrap items-center justify-start gap-3 lg:mt-10">
            <button
              type="button"
              onClick={() => navigate(isAuthenticated ? '/student-portal' : '/register')}
              className="inline-flex min-h-[56px] items-center gap-3 rounded-full border-2 px-8 font-display text-base transition hover:brightness-105 sm:min-h-[60px] sm:px-10 sm:text-lg"
              style={{
                borderColor: THEME.black,
                background: 'var(--color-primary-gradient)',
                color: 'var(--color-on-primary)',
                boxShadow: '0 10px 28px rgba(251, 225, 77, 0.4)',
                letterSpacing: '0.03em',
              }}
            >
              <Play className="h-5 w-5 fill-current sm:h-6 sm:w-6" aria-hidden />
              <span dir="rtl" lang="ar">
                {isAuthenticated ? 'ادخل لمنصتك' : 'اشترك معانا دلوقتي'}
              </span>
            </button>
          </div>
        </div>

        {/* Free portrait — no container */}
        <div className="relative flex min-h-[46vh] items-end justify-center sm:min-h-[52vh] lg:min-h-0 lg:h-full lg:items-end lg:justify-center">
          {/* Floor glow */}
          <div
            className="hero-glow-pulse pointer-events-none absolute bottom-[6%] left-1/2 z-0 h-[28%] w-[70%] -translate-x-1/2 rounded-[100%] blur-3xl lg:bottom-[8%] lg:h-[32%] lg:w-[78%]"
            style={{
              background: `radial-gradient(circle, rgba(${ACCENT_RGB}, 0.45) 0%, rgba(22, 32, 64, 0.35) 42%, transparent 70%)`,
            }}
            aria-hidden
          />

          <div className="relative z-[1] h-[min(52vh,30rem)] w-auto max-w-[min(96vw,32rem)] sm:h-[min(58vh,36rem)] sm:max-w-[min(90vw,38rem)] lg:h-[min(92vh,52rem)] lg:max-w-[min(56vw,46rem)]">
            <div
              className="hero-spin-slow pointer-events-none absolute left-[2%] top-[-2%] z-0 h-[72%] w-[72%] lg:left-[-4%] lg:top-[-4%] lg:h-[78%] lg:w-[78%]"
              aria-hidden
            >
              <img
                src={heroTriangle}
                alt=""
                className="h-full w-full object-contain opacity-75 mix-blend-screen"
                style={{ filter: 'hue-rotate(205deg) saturate(0.7) brightness(1.08)' }}
                draggable={false}
              />
            </div>

            <img
              src={heroTeacher}
              alt={BRAND_TEACHER}
              className="hero-float relative z-[1] h-full w-full object-contain object-bottom"
              style={{
                filter: `drop-shadow(0 30px 50px rgba(7, 11, 24, 0.75)) drop-shadow(0 0 40px rgba(${ACCENT_RGB}, 0.18))`,
              }}
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </div>
      </div>

      {/* Particle wave */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-[34%] max-h-[240px]"
        aria-hidden
      >
        <canvas ref={waveRef} className="absolute inset-0 h-full w-full" />
      </div>
    </section>
  );
};

export default HomeHero;
